import React, { useState } from "react";
import { Helmet } from "react-helmet";
import PageLayout from "../../../components/navigation/PageLayout";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useTenantContext } from "../../../contexts/TenantContext";
import { useAuth } from "../../../contexts/AuthContext";
import { tenantsApi } from "../../../services/api";

/**
 * System Settings – Clear Demo Data
 *
 * Allows GlobalAdmin / TenantAdmin to wipe all operational data for the
 * current tenant while preserving the initial distribution rule
 * (the one with an empty EffectiveFrom date).
 *
 * Cleared entities:
 *   job roles, employees, allocations, distributions,
 *   eligibility rules, distribution rules (except the initial one),
 *   audit logs, transactions, clock records, error logs,
 *   tronc records, sync logs, scheduled emails, approval requests.
 */
const DemoClearPage = () => {
  const {
    selectedTenant,
    currentTenantId,
    needsTenantSelection,
    isGlobalAdmin: isGlobalAdminCtx,
    refreshTenants,
  } = useTenantContext();
  const { isGlobalAdmin, isTenantAdmin } = useAuth();

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    { label: "System Settings", path: "/system/demo-clear" },
    { label: "Clear Demo Data", path: "/system/demo-clear" },
  ];

  const canClear = isGlobalAdmin || isTenantAdmin;
  const isDemoTenant = selectedTenant?.isDemoTenant ?? false;
  const tenantName = selectedTenant?.name || "Current Tenant";
  const confirmPhrase = "CLEAR ALL DATA";

  const handleClearData = async () => {
    if (confirmText !== confirmPhrase) return;

    const tenantId = currentTenantId;
    if (!tenantId) {
      setError("No tenant selected.");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await tenantsApi.clearDemoData(tenantId);
      const data = response?.data ?? response?.Data ?? response;

      if (response?.success ?? response?.Success) {
        setResult(data);
        setShowConfirm(false);
        setConfirmText("");
        // Refresh tenant state to pick up any changes
        refreshTenants?.();
      } else {
        setError(
          response?.message ??
            response?.Message ??
            "Failed to clear demo data.",
        );
      }
    } catch (err) {
      setError(err?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  // Global Admin without a selected tenant
  if (isGlobalAdminCtx && needsTenantSelection) {
    return (
      <>
        <Helmet>
          <title>Clear Demo Data - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="Clear Demo Data"
          description="Remove all operational data for a tenant."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="Building2" size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              As a Global Administrator, please select a tenant from the sidebar
              before clearing demo data.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="ArrowLeft" size={16} />
              <span>Use the tenant selector in the sidebar</span>
            </div>
          </div>
        </PageLayout>
      </>
    );
  }

  // Tenant is not marked as a demo tenant — block the feature
  if (!isDemoTenant) {
    return (
      <>
        <Helmet>
          <title>Clear Demo Data - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="Clear Demo Data"
          description="Remove all operational data for a tenant."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-warning/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="FlaskConical" size={40} className="text-warning" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Not a Demo Tenant</h2>
            <p className="text-muted-foreground mb-4 max-w-md mx-auto">
              The <strong>{tenantName}</strong> tenant is not flagged as a demo
              tenant. Clear Demo Data is only available for tenants marked as
              demo environments.
            </p>
            {isGlobalAdmin && (
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                To enable this feature, go to{" "}
                <strong>Settings &rsaquo; Tenant Management</strong>, edit this
                tenant, and enable the <strong>Demo Tenant</strong> checkbox.
              </p>
            )}
          </div>
        </PageLayout>
      </>
    );
  }

  const clearedEntities = [
    { key: "Employees", icon: "Users", label: "Employees" },
    { key: "JobRoles", icon: "Briefcase", label: "Job Roles" },
    { key: "EmployeeAllocations", icon: "PieChart", label: "Allocations" },
    {
      key: "DistributionRules",
      icon: "Settings2",
      label: "Distribution Rules (except initial)",
    },
    {
      key: "DistributionRuleAllocations",
      icon: "Layers",
      label: "Rule Allocations",
    },
    {
      key: "DistributionApprovalRequests",
      icon: "CheckSquare",
      label: "Approval Requests",
    },
    {
      key: "EligibilityCriteriaVersions",
      icon: "Shield",
      label: "Eligibility Rules",
    },
    { key: "TroncRecords", icon: "PoundSterling", label: "Tronc Records" },
    { key: "Transactions", icon: "Receipt", label: "Transactions" },
    { key: "ClockRecords", icon: "Clock", label: "Clock Records" },
    { key: "EmployeeRoleHistory", icon: "History", label: "Role History" },
    { key: "AuditLogs", icon: "FileText", label: "Audit Logs" },
    { key: "SyncLogs", icon: "RefreshCw", label: "Sync Logs" },
    { key: "ErrorLogs", icon: "AlertTriangle", label: "Error Logs" },
    { key: "ScheduledEmails", icon: "Mail", label: "Scheduled Emails" },
  ];

  return (
    <>
      <Helmet>
        <title>
          Clear Demo Data - {selectedTenant?.name || "Rocket Tronc"}
        </title>
      </Helmet>

      <PageLayout
        title="Clear Demo Data"
        description={
          selectedTenant
            ? `Remove all operational data for ${selectedTenant.name}. This cannot be undone.`
            : "Remove all operational data for the current tenant."
        }
        breadcrumbs={breadcrumbs}
      >
        {/* Warning Banner */}
        <div className="mb-6 bg-error/5 border border-error/20 rounded-lg p-4 flex items-start gap-3">
          <Icon
            name="AlertTriangle"
            size={20}
            className="text-error mt-0.5 shrink-0"
          />
          <div className="text-sm">
            <p className="font-medium text-error mb-1">
              Destructive Operation – Cannot Be Undone
            </p>
            <p className="text-muted-foreground">
              This will permanently delete all operational data for{" "}
              <strong>{tenantName}</strong>. Only the initial distribution rule
              (with no start date) will be preserved. Use this only in
              demo/development environments.
            </p>
          </div>
        </div>

        {/* Success Result */}
        {result && (
          <div className="mb-6 bg-success/5 border border-success/20 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-4">
              <Icon name="CheckCircle" size={20} className="text-success" />
              <h3 className="font-semibold text-success">
                Demo Data Cleared Successfully
              </h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {clearedEntities.map((entity) => {
                const count = result.clearedCounts?.[entity.key] ?? 0;
                if (count === 0) return null;
                return (
                  <div
                    key={entity.key}
                    className="flex items-center gap-2 p-2 bg-white rounded-lg border border-border text-sm"
                  >
                    <Icon
                      name={entity.icon}
                      size={14}
                      className="text-muted-foreground shrink-0"
                    />
                    <div className="min-w-0">
                      <span className="font-semibold">{count}</span>{" "}
                      <span className="text-muted-foreground truncate">
                        {entity.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-6 alert alert-error">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* Entities that will be cleared */}
        <div className="card mb-6">
          <div className="card-header">
            <h3 className="font-semibold flex items-center gap-2">
              <Icon name="Trash2" size={18} className="text-error" />
              Data That Will Be Removed
            </h3>
          </div>
          <div className="card-content p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {clearedEntities.map((entity) => (
                <div
                  key={entity.key}
                  className="flex items-center gap-3 p-3 bg-error/5 rounded-lg border border-error/10"
                >
                  <Icon
                    name={entity.icon}
                    size={16}
                    className="text-error shrink-0"
                  />
                  <span className="text-sm font-medium">{entity.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* What is preserved */}
        <div className="card mb-6">
          <div className="card-header">
            <h3 className="font-semibold flex items-center gap-2">
              <Icon name="ShieldCheck" size={18} className="text-success" />
              Data That Will Be Preserved
            </h3>
          </div>
          <div className="card-content p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                {
                  icon: "Building2",
                  label: "Tenant Configuration",
                  desc: "Company details, subscription, settings",
                },
                {
                  icon: "Settings2",
                  label: "Initial Distribution Rule",
                  desc: "The first rule with no start date (reset to unused state)",
                },
                {
                  icon: "Users",
                  label: "User Accounts",
                  desc: "Login accounts and roles are preserved",
                },
                {
                  icon: "MapPin",
                  label: "Locations",
                  desc: "Location records are preserved",
                },
                {
                  icon: "Plug",
                  label: "Integration Settings",
                  desc: "HR and Till provider configurations",
                },
                {
                  icon: "Mail",
                  label: "User Invitations",
                  desc: "Pending invitations are preserved",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-3 p-3 bg-success/5 rounded-lg border border-success/10"
                >
                  <Icon
                    name={item.icon}
                    size={16}
                    className="text-success shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Button */}
        {canClear && (
          <div className="card p-6">
            {!showConfirm ? (
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div>
                  <h3 className="font-semibold mb-1">
                    Clear All Demo Data for {tenantName}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    This will remove all operational data and reset the tenant
                    to a clean state.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  iconName="Trash2"
                  iconPosition="left"
                  onClick={() => setShowConfirm(true)}
                >
                  Clear Demo Data
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                  <p className="font-semibold text-error mb-2">
                    Are you absolutely sure?
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    This action <strong>cannot be undone</strong>. This will
                    permanently delete all employees, job roles, distributions,
                    tronc records, clock records, and all other operational data
                    for <strong>{tenantName}</strong>.
                  </p>
                  <div className="mb-4">
                    <label className="label text-sm mb-1">
                      Type <strong>{confirmPhrase}</strong> to confirm:
                    </label>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={confirmPhrase}
                      className="input w-full max-w-sm font-mono"
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="destructive"
                      iconName="Trash2"
                      iconPosition="left"
                      onClick={handleClearData}
                      loading={loading}
                      disabled={confirmText !== confirmPhrase || loading}
                    >
                      {loading
                        ? "Clearing Data..."
                        : "I understand, clear all data"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowConfirm(false);
                        setConfirmText("");
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </PageLayout>
    </>
  );
};

export default DemoClearPage;
